import { Apple, Smartphone, Package, Tag, ShieldAlert, Cpu, Layers } from "lucide-react";

/**
 * App Details panel — surfaces the parsed mobile-app package metadata
 * (platform, bundle id, version, min OS, requested permissions) on the
 * results page so an app run reads natively instead of like a URL scan.
 */
export function AppDetails({ platform, meta }) {
  if (!meta) return null;
  const isIOS = platform === "ios";
  const accent = isIOS ? "#007AFF" : "#3DDC84";
  const PlatIcon = isIOS ? Apple : Smartphone;
  const platLabel = isIOS ? "iOS · App Store" : "Android · Google Play";
  const perms = meta.permissions || [];

  const Row = ({ icon: Icon, label, value }) =>
    value ? (
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="w-3.5 h-3.5 text-[#9AA0AC] shrink-0" />
        <span className="sim-mono text-[10px] text-[#9AA0AC] uppercase tracking-wider shrink-0">{label}</span>
        <span className="sim-mono text-[12px] text-[#F5F5F5] truncate">{value}</span>
      </div>
    ) : null;

  return (
    <div className="sim-card rounded-xl p-5 mb-5" data-testid="sim-app-details">
      <div className="flex items-start gap-4 flex-wrap">
        {/* Platform badge */}
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border"
          style={{ backgroundColor: `${accent}1A`, borderColor: `${accent}4D` }}
        >
          <PlatIcon className="w-6 h-6" style={{ color: accent }} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="sim-head text-lg font-bold text-[#F5F5F5] truncate" data-testid="sim-app-name">
              {meta.name || "Uploaded app"}
            </h3>
            <span
              className="sim-mono text-[10px] px-2 py-0.5 rounded-full border"
              style={{ color: accent, borderColor: `${accent}4D` }}
              data-testid="sim-app-platform-badge"
            >
              {platLabel}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            <Row icon={Package} label="Bundle" value={meta.package} />
            <Row icon={Tag} label="Version" value={meta.version} />
            <Row icon={Cpu} label="Min OS" value={meta.min_sdk} />
            <Row icon={Layers} label="Target SDK" value={meta.target_sdk} />
          </div>
        </div>
      </div>

      {/* Requested permissions */}
      <div className="mt-4 pt-4 border-t border-[#1c1c1f]">
        <div className="flex items-center gap-1.5 mb-2">
          <ShieldAlert className="w-3.5 h-3.5 text-[#F59E0B]" />
          <span className="sim-mono text-[10px] text-[#C2C5CE] uppercase tracking-wider">
            Requested permissions {perms.length > 0 && `· ${perms.length}`}
          </span>
        </div>
        {perms.length === 0 ? (
          <p className="sim-mono text-[11px] text-[#8E929C]">None declared / not detected</p>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="sim-app-permissions">
            {perms.map((p) => (
              <span
                key={p}
                className="sim-mono text-[11px] text-[#D4D4D8] bg-[#0A0A0A] border border-[#27272A] rounded-full px-2.5 py-1"
              >
                {p}
              </span>
            ))}
          </div>
        )}
      </div>

      <p className="sim-mono text-[10px] text-[#8E929C] mt-3">
        Static package analysis — icon, metadata &amp; permissions. Not a live runtime simulation.
      </p>
    </div>
  );
}
